import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Icon,
  SimpleGrid,
  Flex,
  useColorModeValue,
  Select,
  Image,
  Skeleton,
  Progress,
  Avatar,
  Divider,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Input,
  InputGroup,
  InputLeftElement,
  keyframes,
  Collapse,
} from '@chakra-ui/react';
import { useCustomToast } from '../../utils/customToast';
import {
  FaStar,
  FaHeart,
  FaChartLine,
  FaTrophy,
  FaMedal,
  FaSmile,
  FaMeh,
  FaFrown,
  FaGlobe,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaCheck,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaCalendarAlt,
} from 'react-icons/fa';
import { SectionLabel } from './DashboardWrapper';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  Cell,
  ReferenceLine,
  PieChart,
  Pie,
} from 'recharts';
import { useUser } from '../../contexts/UserContext';
import { logger } from '../../utils/logger';
import { getBusinessImages } from '../../services/imageService';
import { getYearData } from '../../services/yearService';
import { getHistoricalTraffic } from '../../services/reviewService';
import {
  formatTrafficDisplay,
  calculateSeoScoreGrowth,
  getSeoScoreColor,
  getTrafficValue,
} from '../../utils/competitiveIntelHelpers';

// Pulse animation for 100% progress
const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 8px rgba(72, 187, 120, 0.6), 0 0 16px rgba(72, 187, 120, 0.4);
  }
  50% {
    box-shadow: 0 0 16px rgba(72, 187, 120, 0.8), 0 0 24px rgba(72, 187, 120, 0.6);
  }
`;

// Shimmer animation for progress bar
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Premium Gradient Progress Bar Component
const GradientProgressBar = ({ value, isComplete }) => {
  const bgColor = useColorModeValue('gray.100', 'gray.700');
  
  // Gradient colors based on completion
  const gradientColors = isComplete
    ? 'linear-gradient(90deg, #48BB78 0%, #38A169 50%, #2F855A 100%)'
    : 'linear-gradient(90deg, #4299E1 0%, #667EEA 50%, #9F7AEA 100%)';
  
  // Shimmer overlay for active progress bars
  const shimmerOverlay = 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)';
  
  return (
    <Box
      position="relative"
      w="100%"
      h="10px"
      bg={bgColor}
      borderRadius="full"
      overflow="hidden"
    >
      {/* Progress fill with gradient */}
      <Box
        position="absolute"
        top={0}
        left={0}
        h="100%"
        w={`${Math.min(value, 100)}%`}
        bg={gradientColors}
        borderRadius="full"
        transition="all 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
        animation={isComplete ? `${pulseGlow} 2s ease-in-out infinite` : undefined}
        _after={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: shimmerOverlay,
          backgroundSize: '200% 100%',
          animation: isComplete ? undefined : `${shimmer} 2s linear infinite`,
          borderRadius: 'full',
        }}
      />
      
      {/* Highlight line at top of progress */}
      <Box
        position="absolute"
        top="1px"
        left="2px"
        h="2px"
        w={`calc(${Math.min(value, 100)}% - 4px)`}
        bg="whiteAlpha.400"
        borderRadius="full"
        transition="all 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
      />
    </Box>
  );
};

// Comparison Gauge Chart Component
const ComparisonGaugeChart = ({ favoriteValue, marketAvg, marketLeader, maxValue, format = 'number', unit = '' }) => {
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.500', 'gray.400');

  // Calculate positions on the gauge (0-100 scale)
  const normalizedFav = maxValue > 0 ? (favoriteValue / maxValue) * 100 : 0;
  const normalizedAvg = maxValue > 0 ? (marketAvg / maxValue) * 100 : 0;
  const normalizedLeader = maxValue > 0 ? (marketLeader / maxValue) * 100 : 0;

  // Create gauge segments (5 equal segments with gradient colors)
  const gaugeData = [
    { name: 'Low', value: 20, fill: '#EF4444' },      // Red
    { name: 'Below Avg', value: 20, fill: '#F97316' }, // Orange
    { name: 'Average', value: 20, fill: '#EAB308' },   // Yellow
    { name: 'Good', value: 20, fill: '#84CC16' },      // Lime
    { name: 'Excellent', value: 20, fill: '#22C55E' }, // Green
  ];

  // Calculate needle angles (-90 = 0%, 90 = 100%)
  const favNeedleAngle = -90 + (normalizedFav / 100) * 180;
  const avgNeedleAngle = -90 + (normalizedAvg / 100) * 180;
  const leaderNeedleAngle = -90 + (normalizedLeader / 100) * 180;

  const formatDisplayValue = (value) => {
    if (format === 'percent') return `${value?.toFixed(1) || 0}%`;
    if (format === 'decimal') return value?.toFixed(1) || '0';
    if (format === 'number') return (value || 0).toLocaleString();
    return value;
  };

  return (
    <Box>
      <Text fontSize="sm" fontWeight="semibold" color={textColor} mb={2} textAlign="center">
        Gauge Comparison
      </Text>
      <Box position="relative" height="180px" width="100%">
        {/* The gauge arc */}
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={gaugeData}
              cx="50%"
              cy="85%"
              startAngle={180}
              endAngle={0}
              innerRadius={60}
              outerRadius={90}
              paddingAngle={2}
              cornerRadius={4}
              dataKey="value"
              stroke="none"
            >
              {gaugeData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} opacity={0.85} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* Market Leader Needle (Gold) */}
        <Box
          position="absolute"
          bottom="27px"
          left="50%"
          transformOrigin="bottom center"
          sx={{
            transform: `translateX(-50%) rotate(${leaderNeedleAngle}deg)`,
            transition: 'transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
          zIndex={1}
        >
          <Box
            width="3px"
            height="55px"
            bg="linear-gradient(to top, #ECC94B, transparent)"
            borderRadius="full"
          />
          <Box
            position="absolute"
            top="-8px"
            left="50%"
            transform="translateX(-50%)"
            width="8px"
            height="8px"
            borderRadius="full"
            bg="#ECC94B"
          />
        </Box>

        {/* Market Average Needle (Orange) */}
        <Box
          position="absolute"
          bottom="27px"
          left="50%"
          transformOrigin="bottom center"
          sx={{
            transform: `translateX(-50%) rotate(${avgNeedleAngle}deg)`,
            transition: 'transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
          zIndex={2}
        >
          <Box
            width="3px"
            height="50px"
            bg="linear-gradient(to top, #ED8936, transparent)"
            borderRadius="full"
          />
          <Box
            position="absolute"
            top="-8px"
            left="50%"
            transform="translateX(-50%)"
            width="8px"
            height="8px"
            borderRadius="full"
            bg="#ED8936"
          />
        </Box>

        {/* Your Business Needle (Blue - primary) */}
        <Box
          position="absolute"
          bottom="27px"
          left="50%"
          transformOrigin="bottom center"
          sx={{
            transform: `translateX(-50%) rotate(${favNeedleAngle}deg)`,
            transition: 'transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
          zIndex={3}
        >
          <Box
            width="4px"
            height="65px"
            bg="linear-gradient(to top, #3182CE, transparent)"
            borderRadius="full"
          />
          <Box
            position="absolute"
            top="-10px"
            left="50%"
            transform="translateX(-50%)"
            width="10px"
            height="10px"
            borderRadius="full"
            bg="#3182CE"
            boxShadow="0 0 8px rgba(49, 130, 206, 0.6)"
          />
        </Box>

        {/* Center pivot point */}
        <Box
          position="absolute"
          bottom="22px"
          left="50%"
          transform="translateX(-50%)"
          width="14px"
          height="14px"
          borderRadius="full"
          bg="gray.600"
          boxShadow="0 2px 4px rgba(0,0,0,0.2)"
          zIndex={4}
        />
      </Box>

      {/* Legend */}
      <HStack justify="center" spacing={4} mt={2} flexWrap="wrap">
        <HStack spacing={1}>
          <Box w="10px" h="10px" borderRadius="full" bg="#3182CE" />
          <Text fontSize="xs" color={textColor}>Your Business: {formatDisplayValue(favoriteValue)}{unit}</Text>
        </HStack>
        <HStack spacing={1}>
          <Box w="10px" h="10px" borderRadius="full" bg="#ED8936" />
          <Text fontSize="xs" color={subtleTextColor}>Market Avg: {formatDisplayValue(marketAvg)}{unit}</Text>
        </HStack>
        <HStack spacing={1}>
          <Box w="10px" h="10px" borderRadius="full" bg="#ECC94B" />
          <Text fontSize="xs" color={subtleTextColor}>Leader: {formatDisplayValue(marketLeader)}{unit}</Text>
        </HStack>
      </HStack>
    </Box>
  );
};

// Comparison Bar Chart Component
const ComparisonBarChart = ({ favoriteValue, marketAvg, marketLeader, leaderName, format = 'number', unit = '', title }) => {
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const formatDisplayValue = (value) => {
    if (format === 'percent') return `${value?.toFixed(1) || 0}%`;
    if (format === 'decimal') return value?.toFixed(1) || '0';
    if (format === 'number') return (value || 0).toLocaleString();
    return value;
  };

  const chartData = [
    {
      name: 'Your Business',
      value: favoriteValue || 0,
      fill: '#3182CE',
    },
    {
      name: 'Market Average',
      value: marketAvg || 0,
      fill: '#ED8936',
    },
    {
      name: leaderName ? `Leader: ${leaderName.substring(0, 15)}${leaderName.length > 15 ? '...' : ''}` : 'Market Leader',
      value: marketLeader || 0,
      fill: '#ECC94B',
    },
  ];

  return (
    <Box>
      <Text fontSize="sm" fontWeight="semibold" color={textColor} mb={2} textAlign="center">
        Bar Chart Comparison
      </Text>
      <Box height="200px">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis type="number" tick={{ fontSize: 11, fill: '#4A5568' }} />
            <YAxis 
              type="category" 
              dataKey="name" 
              tick={{ fontSize: 10, fill: '#4A5568' }}
              width={100}
            />
            <RechartsTooltip
              contentStyle={{ 
                backgroundColor: cardBg, 
                border: `1px solid ${borderColor}`, 
                borderRadius: '8px',
                fontSize: '12px'
              }}
              formatter={(value) => [`${formatDisplayValue(value)}${unit}`, '']}
            />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={30}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

// Premium card styling helper
const usePremiumCardStyles = () => {
  const cardBg = useColorModeValue(
    'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
    'linear-gradient(135deg, rgba(45,55,72,0.95) 0%, rgba(45,55,72,0.85) 100%)'
  );
  const cardBgSolid = useColorModeValue('white', 'gray.800');
  const glassBg = useColorModeValue(
    'rgba(255, 255, 255, 0.8)',
    'rgba(45, 55, 72, 0.8)'
  );
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const subtleBorder = useColorModeValue(
    'rgba(255, 255, 255, 0.5)',
    'rgba(255, 255, 255, 0.1)'
  );
  
  return {
    cardBg,
    cardBgSolid,
    glassBg,
    borderColor,
    subtleBorder,
    // Premium shadow with color tint
    premiumShadow: useColorModeValue(
      '0 4px 20px -2px rgba(0, 0, 0, 0.08), 0 2px 8px -2px rgba(0, 0, 0, 0.06)',
      '0 4px 20px -2px rgba(0, 0, 0, 0.4), 0 2px 8px -2px rgba(0, 0, 0, 0.3)'
    ),
    premiumShadowHover: useColorModeValue(
      '0 12px 32px -4px rgba(0, 0, 0, 0.12), 0 4px 16px -4px rgba(0, 0, 0, 0.08)',
      '0 12px 32px -4px rgba(0, 0, 0, 0.5), 0 4px 16px -4px rgba(0, 0, 0, 0.4)'
    ),
    // Glassmorphism backdrop
    backdropFilter: 'blur(12px) saturate(180%)',
  };
};

// Helper to calculate 3-month growth
const calculateThreeMonthGrowth = (monthlyReviewCounts = {}, months = []) => {
  if (months.length < 3) return 0;
  const lastThreeMonths = months.slice(-3);
  return lastThreeMonths.reduce((sum, month) => sum + (monthlyReviewCounts[month] || 0), 0);
};

// Helper to normalize data to 0-100 scale for seasonality comparison
const normalizeToScale = (values) => {
  if (!values || values.length === 0) return values;
  const min = Math.min(...values.filter(v => v !== null && v !== undefined));
  const max = Math.max(...values.filter(v => v !== null && v !== undefined));
  const range = max - min;
  if (range === 0) return values.map(() => 50); // All same values = middle of scale
  return values.map(v => v !== null && v !== undefined ? ((v - min) / range) * 100 : null);
};

// Seasonality Comparison Chart Component
const SeasonalityComparisonChart = ({ 
  industryData, 
  trafficData, 
  reviewData, 
  cardBg, 
  borderColor 
}) => {
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.500', 'gray.400');
  const gridColor = useColorModeValue('#E2E8F0', '#4A5568');
  const tooltipBg = useColorModeValue('white', 'gray.800');
  
  // Merge all data by month and normalize
  const chartData = useMemo(() => {
    if (!industryData || industryData.length === 0) return [];
    
    // Get all unique months from all datasets
    const allMonths = new Set();
    industryData.forEach(d => allMonths.add(d.month));
    trafficData?.forEach(d => allMonths.add(d.month));
    reviewData?.forEach(d => allMonths.add(d.month));
    
    // Sort months chronologically
    const sortedMonths = Array.from(allMonths).sort((a, b) => {
      const dateA = new Date(a + '-01T00:00:00');
      const dateB = new Date(b + '-01T00:00:00');
      return dateA - dateB;
    });
    
    // Take last 12 months for clean visualization
    const last12Months = sortedMonths.slice(-12);
    
    // Create lookup maps
    const industryMap = {};
    const trafficMap = {};
    const reviewMap = {};
    
    industryData.forEach(d => { industryMap[d.month] = d.value; });
    trafficData?.forEach(d => { trafficMap[d.month] = d.value; });
    reviewData?.forEach(d => { reviewMap[d.month] = d.value; });
    
    // Get raw values for normalization
    const industryValues = last12Months.map(m => industryMap[m] || 0);
    const trafficValues = last12Months.map(m => trafficMap[m] || null);
    const reviewValues = last12Months.map(m => reviewMap[m] || 0);
    
    // Normalize each series to 0-100 scale
    const normalizedIndustry = normalizeToScale(industryValues);
    const normalizedTraffic = normalizeToScale(trafficValues.filter(v => v !== null));
    const normalizedReviews = normalizeToScale(reviewValues);
    
    // Track traffic index for sparse traffic data
    let trafficIdx = 0;
    
    return last12Months.map((monthKey, idx) => {
      // Format month for display (e.g., "2024-05" -> "May '24")
      const [year, month] = monthKey.split('-');
      const monthName = new Date(parseInt(year), parseInt(month) - 1, 1)
        .toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      
      // Handle sparse traffic data
      let trafficNorm = null;
      if (trafficMap[monthKey] !== null && trafficMap[monthKey] !== undefined) {
        trafficNorm = normalizedTraffic[trafficIdx];
        trafficIdx++;
      }
      
      return {
        month: monthName,
        monthKey,
        industry: normalizedIndustry[idx],
        traffic: trafficNorm,
        reviews: normalizedReviews[idx],
        // Store raw values for tooltip
        rawIndustry: industryValues[idx],
        rawTraffic: trafficMap[monthKey] || null,
        rawReviews: reviewValues[idx],
      };
    });
  }, [industryData, trafficData, reviewData]);
  
  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    
    const data = payload[0]?.payload;
    
    return (
      <Box
        bg={tooltipBg}
        p={4}
        borderRadius="xl"
        shadow="lg"
        borderWidth="1px"
        borderColor={borderColor}
        minW="200px"
      >
        <Text fontWeight="bold" color={textColor} mb={2}>{label}</Text>
        <VStack align="stretch" spacing={2}>
          {data?.rawIndustry !== undefined && (
            <HStack justify="space-between">
              <HStack spacing={2}>
                <Box w="10px" h="10px" borderRadius="full" bg="#9F7AEA" />
                <Text fontSize="sm" color={subtleTextColor}>Industry</Text>
              </HStack>
              <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                {data.rawIndustry?.toLocaleString()} reviews
              </Text>
            </HStack>
          )}
          {data?.rawTraffic !== null && data?.rawTraffic !== undefined && (
            <HStack justify="space-between">
              <HStack spacing={2}>
                <Box w="10px" h="10px" borderRadius="full" bg="#4299E1" />
                <Text fontSize="sm" color={subtleTextColor}>Web Traffic</Text>
              </HStack>
              <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                {data.rawTraffic?.toLocaleString()} clicks
              </Text>
            </HStack>
          )}
          {data?.rawReviews !== undefined && (
            <HStack justify="space-between">
              <HStack spacing={2}>
                <Box w="10px" h="10px" borderRadius="full" bg="#48BB78" />
                <Text fontSize="sm" color={subtleTextColor}>Your Reviews</Text>
              </HStack>
              <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                {data.rawReviews?.toLocaleString()} reviews
              </Text>
            </HStack>
          )}
        </VStack>
      </Box>
    );
  };

  if (!chartData || chartData.length === 0) {
    return (
      <Flex h="300px" align="center" justify="center">
        <VStack spacing={3}>
          <Icon as={FaCalendarAlt} boxSize={8} color="gray.400" />
          <Text color="gray.400">Not enough data to show seasonality patterns</Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box height="350px">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
        >
          <defs>
            {/* Gradient for industry line */}
            <linearGradient id="industryGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#9F7AEA" stopOpacity={0.4} />
              <stop offset="100%" stopColor="#9F7AEA" stopOpacity={0.05} />
            </linearGradient>
            {/* Gradient for traffic line */}
            <linearGradient id="trafficGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4299E1" stopOpacity={0.4} />
              <stop offset="100%" stopColor="#4299E1" stopOpacity={0.05} />
            </linearGradient>
            {/* Gradient for reviews line */}
            <linearGradient id="reviewsGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#48BB78" stopOpacity={0.4} />
              <stop offset="100%" stopColor="#48BB78" stopOpacity={0.05} />
            </linearGradient>
            {/* Glow effects */}
            <filter id="industryGlow" height="200%">
              <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="#9F7AEA" floodOpacity="0.5"/>
            </filter>
            <filter id="trafficGlow" height="200%">
              <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="#4299E1" floodOpacity="0.5"/>
            </filter>
            <filter id="reviewsGlow" height="200%">
              <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="#48BB78" floodOpacity="0.5"/>
            </filter>
          </defs>
          
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke={gridColor} 
            opacity={0.5}
            vertical={false}
          />
          
          <XAxis 
            dataKey="month" 
            tick={{ fontSize: 11, fill: subtleTextColor }}
            axisLine={{ stroke: gridColor }}
            tickLine={{ stroke: gridColor }}
            dy={10}
          />
          
          <YAxis 
            tick={{ fontSize: 11, fill: subtleTextColor }}
            axisLine={false}
            tickLine={false}
            domain={[0, 100]}
            tickFormatter={(value) => `${value}%`}
            label={{ 
              value: 'Relative Intensity', 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle', fill: subtleTextColor, fontSize: 11 }
            }}
          />
          
          <RechartsTooltip content={<CustomTooltip />} />
          
          <Legend 
            verticalAlign="top"
            height={36}
            iconType="circle"
            iconSize={10}
            formatter={(value) => (
              <span style={{ color: textColor, fontSize: '12px', fontWeight: 500 }}>
                {value}
              </span>
            )}
          />
          
          {/* Industry line */}
          <Line
            type="monotone"
            dataKey="industry"
            name="Industry Trend"
            stroke="#9F7AEA"
            strokeWidth={3}
            dot={false}
            activeDot={{ 
              r: 6, 
              fill: '#9F7AEA', 
              stroke: '#fff', 
              strokeWidth: 2,
              filter: 'url(#industryGlow)'
            }}
            style={{ filter: 'url(#industryGlow)' }}
          />
          
          {/* Traffic line */}
          <Line
            type="monotone"
            dataKey="traffic"
            name="Website Traffic"
            stroke="#4299E1"
            strokeWidth={3}
            dot={false}
            activeDot={{ 
              r: 6, 
              fill: '#4299E1', 
              stroke: '#fff', 
              strokeWidth: 2,
              filter: 'url(#trafficGlow)'
            }}
            connectNulls={true}
            style={{ filter: 'url(#trafficGlow)' }}
          />
          
          {/* Reviews line */}
          <Line
            type="monotone"
            dataKey="reviews"
            name="Your Reviews"
            stroke="#48BB78"
            strokeWidth={3}
            dot={false}
            activeDot={{ 
              r: 6, 
              fill: '#48BB78', 
              stroke: '#fff', 
              strokeWidth: 2,
              filter: 'url(#reviewsGlow)'
            }}
            style={{ filter: 'url(#reviewsGlow)' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

// Comparison Widget Component
const ComparisonWidget = ({
  title,
  icon,
  iconColor,
  favoriteValue,
  marketAvg,
  marketLeader,
  leaderName,
  unit = '',
  format = 'number',
  higherIsBetter = true,
  cardBg,
  borderColor,
  statBg,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const premiumStyles = usePremiumCardStyles();
  const headingColor = useColorModeValue('gray.700', 'gray.100');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.500', 'gray.400');
  const expandedBg = useColorModeValue('gray.50', 'gray.700');
  
  const formatValue = (value) => {
    if (format === 'percent') return `${value?.toFixed(1) || 0}%`;
    if (format === 'decimal') return value?.toFixed(1) || '0';
    if (format === 'number') return (value || 0).toLocaleString();
    return value;
  };

  const getComparisonStatus = (fav, avg) => {
    if (!fav || !avg) return 'neutral';
    const diff = fav - avg;
    const threshold = avg * 0.05; // 5% threshold
    if (Math.abs(diff) < threshold) return 'neutral';
    if (higherIsBetter) return diff > 0 ? 'better' : 'worse';
    return diff < 0 ? 'better' : 'worse';
  };

  const getComparisonIcon = (status) => {
    if (status === 'better') return FaArrowUp;
    if (status === 'worse') return FaArrowDown;
    return FaMinus;
  };

  const getComparisonColor = (status) => {
    if (status === 'better') return 'green.500';
    if (status === 'worse') return 'red.500';
    return 'gray.500';
  };

  const vsAvgStatus = getComparisonStatus(favoriteValue, marketAvg);
  const vsLeaderStatus = getComparisonStatus(favoriteValue, marketLeader);

  const percentOfLeader = marketLeader > 0 ? (favoriteValue / marketLeader) * 100 : 0;
  const isComplete = percentOfLeader >= 100;

  // Calculate max value for gauge normalization
  const maxValue = Math.max(favoriteValue || 0, marketAvg || 0, marketLeader || 0) * 1.1;

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Box
      bg={premiumStyles.glassBg}
      backdropFilter={premiumStyles.backdropFilter}
      borderWidth="1px"
      borderColor={isExpanded ? `${iconColor}.300` : borderColor}
      borderRadius="2xl"
      p={5}
      shadow={isExpanded ? premiumStyles.premiumShadowHover : premiumStyles.premiumShadow}
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      _hover={{ 
        shadow: premiumStyles.premiumShadowHover, 
        transform: isExpanded ? 'none' : 'translateY(-4px)',
        borderColor: `${iconColor}.200`,
      }}
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        bgGradient: `linear(to-r, ${iconColor}.400, ${iconColor}.500, ${iconColor}.400)`,
        opacity: 0.8,
      }}
    >
      {/* Clickable Header */}
      <HStack 
        mb={4} 
        spacing={3} 
        cursor="pointer" 
        onClick={handleToggleExpand}
        _hover={{ opacity: 0.8 }}
        transition="opacity 0.2s"
      >
        <Flex
          w="44px"
          h="44px"
          bgGradient={`linear(135deg, ${iconColor}.100, ${iconColor}.200)`}
          borderRadius="xl"
          justify="center"
          align="center"
          shadow="sm"
        >
          <Icon as={icon} color={`${iconColor}.500`} boxSize={5} />
        </Flex>
        <Heading size="sm" color={headingColor} flex={1}>{title}</Heading>
        <Icon 
          as={isExpanded ? FaChevronUp : FaChevronDown} 
          color={subtleTextColor} 
          boxSize={4}
          transition="transform 0.3s"
        />
      </HStack>

      <VStack spacing={4} align="stretch">
        {/* Your Business Value */}
        <Box 
          bg={statBg} 
          p={4} 
          borderRadius="xl"
          borderWidth="1px"
          borderColor={premiumStyles.subtleBorder}
          cursor="pointer"
          onClick={handleToggleExpand}
          _hover={{ bg: expandedBg }}
          transition="background 0.2s"
        >
          <Text fontSize="xs" color={subtleTextColor} mb={1} fontWeight="medium">Your Business</Text>
          <Text fontSize="2xl" fontWeight="bold" color="blue.500">
            {formatValue(favoriteValue)}{unit}
          </Text>
        </Box>

        {/* Comparison Grid */}
        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
          {/* vs Market Average */}
          <Box 
            bg={statBg} 
            p={3} 
            borderRadius="lg"
            borderWidth="1px"
            borderColor={premiumStyles.subtleBorder}
          >
            <HStack justify="space-between" mb={1}>
              <Text fontSize="xs" color={subtleTextColor}>vs Market Avg</Text>
              <Icon as={getComparisonIcon(vsAvgStatus)} color={getComparisonColor(vsAvgStatus)} boxSize={3} />
            </HStack>
            <Text fontSize="md" fontWeight="semibold" color={textColor}>
              {formatValue(marketAvg)}{unit}
            </Text>
            <Badge
              colorScheme={vsAvgStatus === 'better' ? 'green' : vsAvgStatus === 'worse' ? 'red' : 'gray'}
              fontSize="xs"
              mt={1}
              borderRadius="full"
              px={2}
            >
              {vsAvgStatus === 'better' ? 'Above' : vsAvgStatus === 'worse' ? 'Below' : 'At'} Avg
            </Badge>
          </Box>

          {/* vs Market Leader */}
          <Box 
            bg={statBg} 
            p={3} 
            borderRadius="lg"
            borderWidth="1px"
            borderColor={premiumStyles.subtleBorder}
          >
            <HStack justify="space-between" mb={1}>
              <Text fontSize="xs" color={subtleTextColor}>vs Leader</Text>
              <Icon as={FaTrophy} color="yellow.500" boxSize={3} />
            </HStack>
            <Text fontSize="md" fontWeight="semibold" color={textColor}>
              {formatValue(marketLeader)}{unit}
            </Text>
            <Text fontSize="xs" color={subtleTextColor} mt={1} noOfLines={1}>
              {leaderName}
            </Text>
          </Box>
        </SimpleGrid>

        {/* Progress to Leader - Enhanced */}
        <Box>
          <HStack justify="space-between" mb={2}>
            <Text fontSize="xs" color={subtleTextColor} fontWeight="medium">Progress to Leader</Text>
            <HStack spacing={1}>
              {isComplete && <Icon as={FaTrophy} color="green.400" boxSize={3} />}
              <Text 
                fontSize="sm" 
                fontWeight="bold" 
                color={isComplete ? 'green.500' : 'blue.500'}
              >
                {percentOfLeader.toFixed(0)}%
              </Text>
            </HStack>
          </HStack>
          <GradientProgressBar value={percentOfLeader} isComplete={isComplete} />
          {isComplete && (
            <Text fontSize="xs" color="green.500" mt={1} fontWeight="medium">
              You're the market leader!
            </Text>
          )}
        </Box>

        {/* Expandable Charts Section */}
        <Collapse in={isExpanded} animateOpacity>
          <Box 
            mt={4} 
            pt={4} 
            borderTopWidth="1px" 
            borderTopColor={borderColor}
          >
            <Text fontSize="xs" color={subtleTextColor} mb={4} textAlign="center" fontWeight="medium">
              Click the header to collapse • Detailed comparison charts below
            </Text>
            
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Gauge Chart */}
              <Box 
                bg={expandedBg} 
                p={4} 
                borderRadius="xl"
                borderWidth="1px"
                borderColor={premiumStyles.subtleBorder}
              >
                <ComparisonGaugeChart
                  favoriteValue={favoriteValue}
                  marketAvg={marketAvg}
                  marketLeader={marketLeader}
                  maxValue={maxValue}
                  format={format}
                  unit={unit}
                />
              </Box>

              {/* Bar Chart */}
              <Box 
                bg={expandedBg} 
                p={4} 
                borderRadius="xl"
                borderWidth="1px"
                borderColor={premiumStyles.subtleBorder}
              >
                <ComparisonBarChart
                  favoriteValue={favoriteValue}
                  marketAvg={marketAvg}
                  marketLeader={marketLeader}
                  leaderName={leaderName}
                  format={format}
                  unit={unit}
                  title={title}
                />
              </Box>
            </SimpleGrid>
          </Box>
        </Collapse>

        {/* Click to expand hint */}
        {!isExpanded && (
          <HStack 
            justify="center" 
            cursor="pointer" 
            onClick={handleToggleExpand}
            color={subtleTextColor}
            _hover={{ color: `${iconColor}.500` }}
            transition="color 0.2s"
            mt={2}
          >
            <Text fontSize="xs">Click to view comparison charts</Text>
            <Icon as={FaChevronDown} boxSize={3} />
          </HStack>
        )}
      </VStack>
    </Box>
  );
};

// Competitor Card Component
const CompetitorCard = ({
  business,
  imageUrl,
  reviewGrowth,
  favoriteGrowth,
  position, // 'ahead' or 'behind'
  cardBg,
  borderColor,
  onClick,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const premiumStyles = usePremiumCardStyles();
  const textColor = useColorModeValue('gray.700', 'gray.100');
  const subtleTextColor = useColorModeValue('gray.500', 'gray.400');

  const isAhead = position === 'ahead';
  const accentColor = isAhead ? 'red' : 'green';
  const growthDiff = Math.abs(reviewGrowth - favoriteGrowth);

  return (
    <Box
      bg={premiumStyles.glassBg}
      backdropFilter={premiumStyles.backdropFilter}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      p={4}
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
      transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
      shadow={premiumStyles.premiumShadow}
      _hover={{ 
        shadow: premiumStyles.premiumShadowHover, 
        borderColor: `${accentColor}.300`,
        transform: 'translateY(-3px)',
      }}
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        bgGradient: isAhead 
          ? 'linear(to-r, red.400, orange.400)' 
          : 'linear(to-r, green.400, teal.400)',
      }}
    >
      {/* Position Badge */}
      <Badge
        position="absolute"
        top={3}
        right={3}
        colorScheme={accentColor}
        borderRadius="full"
        px={2}
        py={1}
        fontSize="xs"
        fontWeight="bold"
      >
        {isAhead ? (
          <HStack spacing={1}>
            <Icon as={FaArrowUp} boxSize={2.5} />
            <Text>Ahead</Text>
          </HStack>
        ) : (
          <HStack spacing={1}>
            <Icon as={FaArrowDown} boxSize={2.5} />
            <Text>Behind</Text>
          </HStack>
        )}
      </Badge>

      <HStack spacing={4} align="start">
        {/* Business Image */}
        {imageUrl && !imageError ? (
          <Box 
            w="60px" 
            h="60px" 
            borderRadius="lg" 
            overflow="hidden"
            shadow="sm"
            flexShrink={0}
          >
            {!imageLoaded && <Skeleton w="60px" h="60px" borderRadius="lg" />}
            <Image
              src={imageUrl}
              alt={business.name}
              w="60px"
              h="60px"
              objectFit="cover"
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              opacity={imageLoaded ? 1 : 0}
            />
          </Box>
        ) : (
          <Avatar name={business.name} size="md" borderRadius="lg" />
        )}

        {/* Business Info */}
        <VStack align="start" spacing={1} flex={1} minW={0}>
          <Text fontWeight="bold" fontSize="md" noOfLines={1} color={textColor}>
            {business.name}
          </Text>
          <HStack spacing={2}>
            <HStack spacing={1}>
              <Icon as={FaStar} color="yellow.400" boxSize={3} />
              <Text fontSize="sm" color={subtleTextColor}>
                {business.rating_avg?.toFixed(1) || 'N/A'}
              </Text>
            </HStack>
            <Text fontSize="sm" color={subtleTextColor}>
              ({business.rating_count?.toLocaleString() || 0} reviews)
            </Text>
          </HStack>
        </VStack>
      </HStack>

      {/* Growth Comparison */}
      <Box mt={4} pt={3} borderTopWidth="1px" borderTopColor={borderColor}>
        <HStack justify="space-between" mb={2}>
          <Text fontSize="xs" color={subtleTextColor} fontWeight="medium">
            3-Month Review Growth
          </Text>
          <Badge 
            colorScheme={isAhead ? 'red' : 'green'} 
            fontSize="xs"
            borderRadius="full"
          >
            {isAhead ? '+' : '-'}{growthDiff} {growthDiff === 1 ? 'review' : 'reviews'} {isAhead ? 'more' : 'less'}
          </Badge>
        </HStack>
        <HStack spacing={3}>
          <Box flex={1}>
            <HStack justify="space-between" mb={1}>
              <Text fontSize="xs" color={subtleTextColor}>Their Growth</Text>
              <Text fontSize="sm" fontWeight="bold" color={`${accentColor}.500`}>
                +{reviewGrowth}
              </Text>
            </HStack>
            <Progress 
              value={(reviewGrowth / Math.max(reviewGrowth, favoriteGrowth, 1)) * 100} 
              size="sm" 
              colorScheme={accentColor}
              borderRadius="full"
            />
          </Box>
        </HStack>
      </Box>
    </Box>
  );
};

// Business Selector Card
const BusinessSelectorCard = ({
  business,
  imageUrl,
  isSelected,
  onSelect,
  cardBg,
  borderColor,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const premiumStyles = usePremiumCardStyles();
  const textColor = useColorModeValue('gray.700', 'gray.100');
  const subtleTextColor = useColorModeValue('gray.500', 'gray.400');

  return (
    <Box
      bg={premiumStyles.glassBg}
      backdropFilter={premiumStyles.backdropFilter}
      borderWidth={isSelected ? '2px' : '1px'}
      borderColor={isSelected ? 'blue.400' : borderColor}
      borderRadius="xl"
      p={3}
      cursor="pointer"
      onClick={() => onSelect(business)}
      transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
      shadow={isSelected ? premiumStyles.premiumShadowHover : 'sm'}
      _hover={{ 
        shadow: premiumStyles.premiumShadow, 
        borderColor: 'blue.300',
        transform: 'translateY(-2px)',
      }}
      position="relative"
    >
      {isSelected && (
        <Badge
          position="absolute"
          top={-2}
          right={-2}
          colorScheme="blue"
          borderRadius="full"
          px={2}
          py={1}
          shadow="sm"
        >
          <Icon as={FaCheck} boxSize={3} />
        </Badge>
      )}
      <HStack spacing={3}>
        {imageUrl && !imageError ? (
          <Box 
            w="50px" 
            h="50px" 
            borderRadius="lg" 
            overflow="hidden"
            shadow="sm"
          >
            {!imageLoaded && <Skeleton w="50px" h="50px" borderRadius="lg" />}
            <Image
              src={imageUrl}
              alt={business.name}
              w="50px"
              h="50px"
              objectFit="cover"
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              opacity={imageLoaded ? 1 : 0}
            />
          </Box>
        ) : (
          <Avatar name={business.name} size="md" borderRadius="lg" />
        )}
        <VStack align="start" spacing={0} flex={1} minW={0}>
          <Text fontWeight="semibold" fontSize="sm" noOfLines={1} color={textColor}>
            {business.name}
          </Text>
          <Text fontSize="xs" color={subtleTextColor}>
            {business.rating_count?.toLocaleString() || 0} reviews
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
};

const FavoriteDashboard = ({ reviewsData, demoMode, demoData }) => {
  const { favoriteBusinesses, toggleFavorite, favoriteUris } = useUser();
  const [businessImages, setBusinessImages] = useState({});
  const [yearData, setYearData] = useState([]);
  const [trafficHistory, setTrafficHistory] = useState({});
  const [selectedBusinessUri, setSelectedBusinessUri] = useState(null);
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const { isOpen: isSelectModalOpen, onOpen: onSelectModalOpen, onClose: onSelectModalClose } = useDisclosure();
  const toast = useCustomToast();

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const accentColor = useColorModeValue('blue.500', 'blue.400');
  
  // Premium styling variables
  const glassBg = useColorModeValue(
    'rgba(255, 255, 255, 0.85)',
    'rgba(45, 55, 72, 0.85)'
  );
  const backdropFilter = 'blur(12px) saturate(180%)';
  const premiumShadow = useColorModeValue(
    '0 4px 20px -2px rgba(0, 0, 0, 0.08), 0 2px 8px -2px rgba(0, 0, 0, 0.06)',
    '0 4px 20px -2px rgba(0, 0, 0, 0.4), 0 2px 8px -2px rgba(0, 0, 0, 0.3)'
  );
  const premiumShadowHover = useColorModeValue(
    '0 12px 32px -4px rgba(0, 0, 0, 0.12), 0 4px 16px -4px rgba(0, 0, 0, 0.08)',
    '0 12px 32px -4px rgba(0, 0, 0, 0.5), 0 4px 16px -4px rgba(0, 0, 0, 0.4)'
  );
  const subtleTextColor = useColorModeValue('gray.500', 'gray.400');
  const headingTextColor = useColorModeValue('gray.700', 'gray.100');
  const insightBg = useColorModeValue('purple.50', 'purple.900');
  const insightBorderColor = useColorModeValue('purple.100', 'purple.700');

  // Set selected business to first favorite on load; in demo mode use first business from reviewsData
  useEffect(() => {
    if (selectedBusinessUri) return;
    if (favoriteBusinesses?.length > 0) {
      setSelectedBusinessUri(favoriteBusinesses[0].maps_uri);
    } else if (demoMode && reviewsData?.length > 0) {
      setSelectedBusinessUri(reviewsData[0].maps_uri);
    }
  }, [favoriteBusinesses, selectedBusinessUri, demoMode, reviewsData]);

  // Fetch business images
  useEffect(() => {
    if (demoMode) return;
    let mounted = true;
    const fetchImages = async () => {
      try {
        const images = await getBusinessImages();
        if (mounted && images) {
          const imageMap = {};
          images.forEach(img => {
            if (img.maps_uri && img.high_res_image_url_fetched) {
              imageMap[img.maps_uri] = img.high_res_image_url_fetched;
            }
          });
          setBusinessImages(imageMap);
        }
      } catch (error) {
        logger.error('Error fetching business images:', error);
      }
    };
    fetchImages();
    return () => { mounted = false; };
  }, [demoMode]);

  // Fetch year data
  useEffect(() => {
    if (demoMode && demoData) {
      setYearData(demoData.yearData || []);
      return;
    }
    let mounted = true;
    const fetchYearData = async () => {
      try {
        const data = await getYearData();
        if (mounted) setYearData(data);
      } catch (error) {
        logger.error('Error fetching year data:', error);
      }
    };
    fetchYearData();
    return () => { mounted = false; };
  }, [demoMode, demoData]);

  // Fetch traffic data
  useEffect(() => {
    if (demoMode && demoData?.modalTrafficHistory) {
      setTrafficHistory(demoData.modalTrafficHistory);
      return;
    }
    if (demoMode) return;
    let mounted = true;
    const fetchTrafficData = async () => {
      try {
        const result = await getHistoricalTraffic();
        if (mounted && result?.businesses) {
          const trafficMap = {};
          result.businesses.forEach(business => {
            if (business.mapsUri && business.timeSeries) {
              const chartData = business.timeSeries
                .sort((a, b) => a.yearMonth.localeCompare(b.yearMonth))
                .slice(-12)
                .map(point => ({
                  month: new Date(point.yearMonth + '-01T00:00:00').toLocaleDateString('en-US', {
                    month: 'short',
                    year: '2-digit'
                  }),
                  organicClicks: point.organicClicks || 0
                }));
              trafficMap[business.mapsUri] = chartData;
            }
          });
          setTrafficHistory(trafficMap);
        }
      } catch (error) {
        logger.error('Error fetching traffic history:', error);
      }
    };
    fetchTrafficData();
    return () => { mounted = false; };
  }, [demoMode, demoData]);

  // Combine all businesses with year data
  const allBusinessesWithData = useMemo(() => {
    if (!reviewsData || reviewsData.length === 0) return [];
    return reviewsData.map(business => {
      const yearInfo = yearData.find(yd => yd.maps_uri === business.maps_uri);
      return {
        ...business,
        historicalData: yearInfo?.data || {
          monthlyReviewCounts: {},
          monthlyRatings: {},
          sentimentBreakdown: { positive: 0, neutral: 0, negative: 0 }
        }
      };
    });
  }, [reviewsData, yearData]);

  // Get selected favorite business with full data
  const selectedBusiness = useMemo(() => {
    if (!selectedBusinessUri || !allBusinessesWithData.length) return null;
    return allBusinessesWithData.find(b => b.maps_uri === selectedBusinessUri);
  }, [selectedBusinessUri, allBusinessesWithData]);

  // Calculate market metrics
  const marketMetrics = useMemo(() => {
    if (!allBusinessesWithData.length) return null;

    // Total Reviews
    const totalReviewsAll = allBusinessesWithData.map(b => b.rating_count || 0);
    const avgTotalReviews = totalReviewsAll.reduce((a, b) => a + b, 0) / totalReviewsAll.length;
    const maxTotalReviews = Math.max(...totalReviewsAll);
    const totalReviewsLeader = allBusinessesWithData.find(b => b.rating_count === maxTotalReviews);

    // Rating
    const ratingsAll = allBusinessesWithData.map(b => b.rating_avg || 0).filter(r => r > 0);
    const avgRating = ratingsAll.reduce((a, b) => a + b, 0) / ratingsAll.length;
    const maxRating = Math.max(...ratingsAll);
    const ratingLeader = allBusinessesWithData.find(b => b.rating_avg === maxRating);

    // Review Growth (3-month)
    const growthAll = allBusinessesWithData.map(b => {
      const months = Object.keys(b.historicalData?.monthlyReviewCounts || {}).sort();
      return calculateThreeMonthGrowth(b.historicalData?.monthlyReviewCounts, months);
    });
    const avgGrowth = growthAll.reduce((a, b) => a + b, 0) / growthAll.length;
    const maxGrowth = Math.max(...growthAll);
    const growthLeaderIndex = growthAll.indexOf(maxGrowth);
    const growthLeader = allBusinessesWithData[growthLeaderIndex];

    // Sentiment Score
    const sentimentAll = allBusinessesWithData.map(b => {
      const sent = b.historicalData?.sentimentBreakdown;
      if (!sent) return 0;
      const total = sent.positive + sent.neutral + sent.negative;
      return total > 0 ? ((sent.positive + sent.neutral * 0.5) / total) * 100 : 0;
    });
    const avgSentiment = sentimentAll.reduce((a, b) => a + b, 0) / sentimentAll.length;
    const maxSentiment = Math.max(...sentimentAll);
    const sentimentLeaderIndex = sentimentAll.indexOf(maxSentiment);
    const sentimentLeader = allBusinessesWithData[sentimentLeaderIndex];

    // Web Traffic
    const trafficAll = allBusinessesWithData.map(b => getTrafficValue(b) || 0).filter(t => t > 0);
    const avgTraffic = trafficAll.length > 0 ? trafficAll.reduce((a, b) => a + b, 0) / trafficAll.length : 0;
    const maxTraffic = trafficAll.length > 0 ? Math.max(...trafficAll) : 0;
    const trafficLeader = allBusinessesWithData.find(b => getTrafficValue(b) === maxTraffic);

    // SEO Score
    const seoAll = allBusinessesWithData.map(b => {
      const seoData = calculateSeoScoreGrowth(b, trafficHistory[b.maps_uri]);
      return seoData.currentScore;
    }).filter(s => s > 0);
    const avgSeo = seoAll.length > 0 ? seoAll.reduce((a, b) => a + b, 0) / seoAll.length : 0;
    const maxSeo = seoAll.length > 0 ? Math.max(...seoAll) : 0;
    const seoLeader = allBusinessesWithData.find(b => {
      const score = calculateSeoScoreGrowth(b, trafficHistory[b.maps_uri]).currentScore;
      return score === maxSeo;
    });

    return {
      totalReviews: { avg: avgTotalReviews, max: maxTotalReviews, leader: totalReviewsLeader },
      rating: { avg: avgRating, max: maxRating, leader: ratingLeader },
      growth: { avg: avgGrowth, max: maxGrowth, leader: growthLeader },
      sentiment: { avg: avgSentiment, max: maxSentiment, leader: sentimentLeader },
      traffic: { avg: avgTraffic, max: maxTraffic, leader: trafficLeader },
      seo: { avg: avgSeo, max: maxSeo, leader: seoLeader },
    };
  }, [allBusinessesWithData, trafficHistory]);

  // Calculate favorite business metrics
  const favoriteMetrics = useMemo(() => {
    if (!selectedBusiness) return null;

    const months = Object.keys(selectedBusiness.historicalData?.monthlyReviewCounts || {}).sort();
    const growth = calculateThreeMonthGrowth(selectedBusiness.historicalData?.monthlyReviewCounts, months);

    const sent = selectedBusiness.historicalData?.sentimentBreakdown;
    const sentTotal = sent ? sent.positive + sent.neutral + sent.negative : 0;
    const sentiment = sentTotal > 0 ? ((sent.positive + sent.neutral * 0.5) / sentTotal) * 100 : 0;

    const traffic = getTrafficValue(selectedBusiness) || 0;
    const seoData = calculateSeoScoreGrowth(selectedBusiness, trafficHistory[selectedBusiness.maps_uri]);

    return {
      totalReviews: selectedBusiness.rating_count || 0,
      rating: selectedBusiness.rating_avg || 0,
      growth,
      sentiment,
      traffic,
      seo: seoData.currentScore,
      positiveReviews: sent?.positive || 0,
      neutralReviews: sent?.neutral || 0,
      negativeReviews: sent?.negative || 0,
    };
  }, [selectedBusiness, trafficHistory]);

  // Growth chart data
  const growthChartData = useMemo(() => {
    if (!selectedBusiness?.historicalData?.monthlyReviewCounts) return [];
    const monthly = selectedBusiness.historicalData.monthlyReviewCounts;
    return Object.entries(monthly)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12)
      .map(([month, count]) => ({
        month: new Date(month + '-01T00:00:00').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        reviews: count,
      }));
  }, [selectedBusiness]);

  // Industry seasonality data - aggregate all businesses' monthly reviews
  const industrySeasonalityData = useMemo(() => {
    if (!allBusinessesWithData || allBusinessesWithData.length === 0) return [];
    
    // Aggregate monthly reviews across all businesses
    const monthlyTotals = {};
    
    allBusinessesWithData.forEach(business => {
      const monthlyReviewCounts = business.historicalData?.monthlyReviewCounts || {};
      Object.entries(monthlyReviewCounts).forEach(([month, count]) => {
        if (!monthlyTotals[month]) {
          monthlyTotals[month] = 0;
        }
        monthlyTotals[month] += count || 0;
      });
    });
    
    // Convert to array and sort by date
    return Object.entries(monthlyTotals)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, value]) => ({ month, value }));
  }, [allBusinessesWithData]);

  // Website traffic seasonality data - from selected business's traffic history
  const trafficSeasonalityData = useMemo(() => {
    if (!selectedBusiness || !trafficHistory[selectedBusiness.maps_uri]) return [];
    
    const traffic = trafficHistory[selectedBusiness.maps_uri];
    // Convert month format from "Jan '24" back to "2024-01" for alignment
    return traffic.map(item => {
      // Parse the formatted month back to YYYY-MM
      const parts = item.month.split(' ');
      const monthName = parts[0];
      const yearShort = parts[1]?.replace("'", '') || '';
      const monthNum = new Date(Date.parse(monthName + ' 1, 2000')).getMonth() + 1;
      const year = yearShort ? `20${yearShort}` : new Date().getFullYear();
      const monthKey = `${year}-${String(monthNum).padStart(2, '0')}`;
      
      return {
        month: monthKey,
        value: item.organicClicks || 0
      };
    });
  }, [selectedBusiness, trafficHistory]);

  // Review seasonality data - from selected business's historical data
  const reviewSeasonalityData = useMemo(() => {
    if (!selectedBusiness?.historicalData?.monthlyReviewCounts) return [];
    
    const monthly = selectedBusiness.historicalData.monthlyReviewCounts;
    return Object.entries(monthly)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, value]) => ({ month, value }));
  }, [selectedBusiness]);

  // Calculate closest competitors based on review growth
  const closestCompetitors = useMemo(() => {
    if (!selectedBusiness || !allBusinessesWithData.length || !favoriteMetrics) {
      return { ahead: null, behind: null };
    }

    // Calculate growth for all businesses and create sorted list
    const businessesWithGrowth = allBusinessesWithData
      .filter(b => b.maps_uri !== selectedBusiness.maps_uri) // Exclude the favorite
      .map(business => {
        const months = Object.keys(business.historicalData?.monthlyReviewCounts || {}).sort();
        const growth = calculateThreeMonthGrowth(business.historicalData?.monthlyReviewCounts, months);
        return {
          ...business,
          reviewGrowth: growth,
        };
      })
      .sort((a, b) => b.reviewGrowth - a.reviewGrowth); // Sort by growth descending (highest first)

    const favoriteGrowth = favoriteMetrics.growth;

    // Find the business just ahead (higher growth than favorite)
    // This is the business with the smallest growth that's still higher than favorite
    const businessesAhead = businessesWithGrowth
      .filter(b => b.reviewGrowth > favoriteGrowth)
      .sort((a, b) => a.reviewGrowth - b.reviewGrowth); // Sort ascending to get closest
    const closestAhead = businessesAhead.length > 0 ? businessesAhead[0] : null;

    // Find the business just behind (lower growth than favorite)
    // This is the business with the highest growth that's still lower than favorite
    const businessesBehind = businessesWithGrowth
      .filter(b => b.reviewGrowth < favoriteGrowth)
      .sort((a, b) => b.reviewGrowth - a.reviewGrowth); // Sort descending to get closest
    const closestBehind = businessesBehind.length > 0 ? businessesBehind[0] : null;

    // If no one is ahead, show the one with equal growth (if any)
    // If no one is behind, show the one with equal growth (if any)
    const businessesEqual = businessesWithGrowth.filter(b => b.reviewGrowth === favoriteGrowth);
    
    return {
      ahead: closestAhead || (businessesEqual.length > 0 && !closestBehind ? businessesEqual[0] : null),
      behind: closestBehind || (businessesEqual.length > 1 ? businessesEqual[1] : (businessesEqual.length > 0 && !closestAhead ? businessesEqual[0] : null)),
    };
  }, [selectedBusiness, allBusinessesWithData, favoriteMetrics]);

  // Filter businesses for modal search
  const filteredBusinessesForModal = useMemo(() => {
    if (!allBusinessesWithData || allBusinessesWithData.length === 0) return [];
    
    if (!modalSearchQuery.trim()) {
      return allBusinessesWithData;
    }
    
    const query = modalSearchQuery.toLowerCase();
    return allBusinessesWithData.filter((business) =>
      business.name?.toLowerCase().includes(query) ||
      business.address?.toLowerCase().includes(query)
    );
  }, [allBusinessesWithData, modalSearchQuery]);

  // Handle selecting a favorite (users can only have one)
  const handleSelectFavorite = async (business) => {
    setSelectedBusinessUri(business.maps_uri);
    onSelectModalClose();
    setModalSearchQuery('');
    
    // If not already the favorite, set it (replaces any existing favorite)
    if (!favoriteUris?.includes(business.maps_uri)) {
      try {
        const result = await toggleFavorite(business.maps_uri);
        if (result.isNowFavorited) {
          toast({
            title: 'Great Choice! 🎉',
            description: 'Your favorite will now be ⭐ starred on all dashboards',
            status: 'success',
            duration: 3500,
            isClosable: true,
          });
        }
      } catch (error) {
        logger.error('Error setting favorite:', error);
        toast({
          title: 'Error',
          description: 'Failed to set favorite',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  // Empty state - no businesses available
  if (!reviewsData || reviewsData.length === 0) {
    return (
      <Box p={6}>
        {!demoMode && <SectionLabel description="Compare your favorite business against market averages and leaders.">
          My Favorite
        </SectionLabel>}
        <Box
          bg={glassBg}
          backdropFilter={backdropFilter}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="2xl"
          p={12}
          textAlign="center"
          shadow={premiumShadow}
        >
          <VStack spacing={4}>
            <Flex
              w="80px"
              h="80px"
              bgGradient="linear(135deg, gray.100, gray.200)"
              borderRadius="full"
              justify="center"
              align="center"
              shadow="sm"
            >
              <Icon as={FaHeart} boxSize={10} color="gray.400" />
            </Flex>
            <Heading size="md" color={subtleTextColor}>No businesses available</Heading>
            <Text color="gray.400" maxW="md">
              Market data is still loading. Please try again later.
            </Text>
          </VStack>
        </Box>
      </Box>
    );
  }

  // No favorite selected state - show prompt to select
  if (!selectedBusiness) {
    return (
      <Box p={6}>
        {!demoMode && <SectionLabel description="Select a business to compare against market averages and leaders.">
          My Favorite
        </SectionLabel>}

        <Box
          bg={glassBg}
          backdropFilter={backdropFilter}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="2xl"
          p={12}
          textAlign="center"
          shadow={premiumShadow}
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            bgGradient: 'linear(to-r, yellow.400, orange.400, pink.400)',
          }}
        >
          <VStack spacing={6}>
            <Flex
              w="80px"
              h="80px"
              bgGradient="linear(135deg, yellow.100, orange.100)"
              borderRadius="full"
              justify="center"
              align="center"
              shadow="md"
            >
              <Icon as={FaStar} boxSize={10} color="yellow.500" />
            </Flex>
            <Heading size="lg" color={headingTextColor}>Select Your Favorite Business</Heading>
            <Text color={subtleTextColor} textAlign="center" maxW="lg">
              Choose one business to track. See how it compares to market averages and the market leader across all key metrics.
            </Text>
            <Button
              colorScheme="blue"
              size="lg"
              leftIcon={<Icon as={FaHeart} />}
              onClick={onSelectModalOpen}
              borderRadius="xl"
              shadow="md"
              _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
              transition="all 0.2s"
            >
              Choose Your Favorite
            </Button>
          </VStack>
        </Box>

        {/* Business Selector Modal */}
        <Modal 
          isOpen={isSelectModalOpen} 
          onClose={() => {
            onSelectModalClose();
            setModalSearchQuery('');
          }}
          size="4xl"
          scrollBehavior="inside"
        >
          <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
          <ModalContent maxH="80vh" borderRadius="2xl" shadow="2xl">
            <ModalHeader borderBottom="1px" borderColor={borderColor}>
              <VStack align="start" spacing={2}>
                <Heading size="md" color={headingTextColor}>Select Your Favorite Business</Heading>
                <Text fontSize="sm" color={subtleTextColor} fontWeight="normal">
                  Choose one business to track and compare against the market.
                </Text>
              </VStack>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6} pt={4}>
              {/* Search Input */}
              <InputGroup mb={4}>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FaSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search businesses..."
                  value={modalSearchQuery}
                  onChange={(e) => setModalSearchQuery(e.target.value)}
                  borderRadius="xl"
                  _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
                />
              </InputGroup>
              
              {/* Business Grid */}
              <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
                {filteredBusinessesForModal.slice(0, 30).map(business => (
                  <BusinessSelectorCard
                    key={business.maps_uri}
                    business={business}
                    imageUrl={businessImages[business.maps_uri]}
                    isSelected={business.maps_uri === selectedBusinessUri}
                    onSelect={handleSelectFavorite}
                    cardBg={cardBg}
                    borderColor={borderColor}
                  />
                ))}
              </SimpleGrid>
              
              {filteredBusinessesForModal.length === 0 && (
                <Box textAlign="center" py={8}>
                  <Text color={subtleTextColor}>No businesses match your search</Text>
                </Box>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    );
  }

  return (
    <Box p={6}>
      {/* Section Label */}
      {!demoMode && <SectionLabel description="Compare your favorite business against market averages and leaders.">
        My Favorite
      </SectionLabel>}

      {/* Hero Card - Selected Business */}
      <Box
        bg={glassBg}
        backdropFilter={backdropFilter}
        borderWidth="2px"
        borderColor="blue.400"
        borderRadius="2xl"
        p={6}
        mb={8}
        shadow={premiumShadowHover}
        position="relative"
        overflow="hidden"
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        _hover={{ shadow: '0 16px 40px -8px rgba(66, 153, 225, 0.3), 0 8px 20px -8px rgba(0, 0, 0, 0.1)' }}
      >
        {/* Gradient accent with animation */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="4px"
          bgGradient="linear(to-r, blue.400, purple.500, pink.400)"
          bgSize="200% 100%"
        />
        {/* Subtle gradient overlay */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bgGradient="linear(135deg, rgba(66, 153, 225, 0.03) 0%, transparent 50%, rgba(159, 122, 234, 0.03) 100%)"
          pointerEvents="none"
        />

        <Flex direction={{ base: 'column', md: 'row' }} gap={6} align="center">
          {/* Business Image */}
          <Box position="relative">
            {businessImages[selectedBusiness.maps_uri] ? (
              <Box w="120px" h="120px" borderRadius="xl" overflow="hidden" shadow="md">
                <Image
                  src={businessImages[selectedBusiness.maps_uri]}
                  alt={selectedBusiness.name}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                />
              </Box>
            ) : (
              <Avatar name={selectedBusiness.name} size="2xl" />
            )}
            <Badge
              position="absolute"
              bottom={-2}
              right={-2}
              colorScheme="yellow"
              borderRadius="full"
              p={2}
            >
              <Icon as={FaStar} boxSize={4} />
            </Badge>
          </Box>

          {/* Business Info */}
          <VStack align={{ base: 'center', md: 'start' }} spacing={2} flex={1}>
            <Heading size="lg">{selectedBusiness.name}</Heading>
            <HStack spacing={4} flexWrap="wrap" justify={{ base: 'center', md: 'start' }}>
              <Badge colorScheme="blue" fontSize="sm" px={3} py={1}>
                {selectedBusiness.primary_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Business'}
              </Badge>
              <HStack>
                <Icon as={FaStar} color="yellow.400" />
                <Text fontWeight="bold">{selectedBusiness.rating_avg?.toFixed(1) || 'N/A'}</Text>
                <Text color="gray.500">({selectedBusiness.rating_count?.toLocaleString() || 0} reviews)</Text>
              </HStack>
            </HStack>
            <Text color="gray.500" fontSize="sm" noOfLines={1}>
              {selectedBusiness.address || 'No address available'}
            </Text>
          </VStack>

          {/* Change Favorite Button */}
          <Button
            variant="outline"
            colorScheme="blue"
            size="sm"
            onClick={onSelectModalOpen}
          >
            Change Favorite
          </Button>
        </Flex>
      </Box>

      {/* Quick Stats Row - Premium Styling */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={8}>
        <Box 
          bg={glassBg} 
          backdropFilter={backdropFilter}
          p={5} 
          borderRadius="2xl" 
          borderWidth="1px" 
          borderColor={borderColor} 
          textAlign="center"
          shadow={premiumShadow}
          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
          _hover={{ shadow: premiumShadowHover, transform: 'translateY(-3px)' }}
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            bgGradient: 'linear(to-r, blue.400, blue.500)',
          }}
        >
          <Text fontSize="3xl" fontWeight="bold" bgGradient="linear(to-r, blue.400, blue.600)" bgClip="text">{favoriteMetrics?.totalReviews.toLocaleString()}</Text>
          <Text fontSize="sm" color={subtleTextColor} fontWeight="medium">Total Reviews</Text>
        </Box>
        <Box 
          bg={glassBg} 
          backdropFilter={backdropFilter}
          p={5} 
          borderRadius="2xl" 
          borderWidth="1px" 
          borderColor={borderColor} 
          textAlign="center"
          shadow={premiumShadow}
          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
          _hover={{ shadow: premiumShadowHover, transform: 'translateY(-3px)' }}
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            bgGradient: 'linear(to-r, yellow.400, orange.400)',
          }}
        >
          <Text fontSize="3xl" fontWeight="bold" bgGradient="linear(to-r, yellow.400, orange.500)" bgClip="text">{favoriteMetrics?.rating.toFixed(1)}</Text>
          <Text fontSize="sm" color={subtleTextColor} fontWeight="medium">Rating</Text>
        </Box>
        <Box 
          bg={glassBg} 
          backdropFilter={backdropFilter}
          p={5} 
          borderRadius="2xl" 
          borderWidth="1px" 
          borderColor={borderColor} 
          textAlign="center"
          shadow={premiumShadow}
          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
          _hover={{ shadow: premiumShadowHover, transform: 'translateY(-3px)' }}
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            bgGradient: 'linear(to-r, green.400, teal.400)',
          }}
        >
          <Text fontSize="3xl" fontWeight="bold" bgGradient="linear(to-r, green.400, teal.500)" bgClip="text">+{favoriteMetrics?.growth}</Text>
          <Text fontSize="sm" color={subtleTextColor} fontWeight="medium">3-Month Growth</Text>
        </Box>
        <Box 
          bg={glassBg} 
          backdropFilter={backdropFilter}
          p={5} 
          borderRadius="2xl" 
          borderWidth="1px" 
          borderColor={borderColor} 
          textAlign="center"
          shadow={premiumShadow}
          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
          _hover={{ shadow: premiumShadowHover, transform: 'translateY(-3px)' }}
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            bgGradient: 'linear(to-r, purple.400, pink.400)',
          }}
        >
          <Text fontSize="3xl" fontWeight="bold" bgGradient="linear(to-r, purple.400, pink.500)" bgClip="text">{favoriteMetrics?.sentiment.toFixed(0)}%</Text>
          <Text fontSize="sm" color={subtleTextColor} fontWeight="medium">Sentiment</Text>
        </Box>
      </SimpleGrid>

      {/* Comparison Widgets Grid */}
      <Heading size="md" mb={4} color={headingTextColor}>Performance Comparison</Heading>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} mb={8}>
        {/* Review Growth */}
        <ComparisonWidget
          title="Review Growth"
          icon={FaChartLine}
          iconColor="green"
          favoriteValue={favoriteMetrics?.growth}
          marketAvg={marketMetrics?.growth.avg}
          marketLeader={marketMetrics?.growth.max}
          leaderName={marketMetrics?.growth.leader?.name}
          unit=" reviews"
          format="number"
          cardBg={cardBg}
          borderColor={borderColor}
          statBg={statBg}
        />

        {/* Total Reviews */}
        <ComparisonWidget
          title="Total Reviews"
          icon={FaStar}
          iconColor="yellow"
          favoriteValue={favoriteMetrics?.totalReviews}
          marketAvg={marketMetrics?.totalReviews.avg}
          marketLeader={marketMetrics?.totalReviews.max}
          leaderName={marketMetrics?.totalReviews.leader?.name}
          format="number"
          cardBg={cardBg}
          borderColor={borderColor}
          statBg={statBg}
        />

        {/* Customer Sentiment */}
        <ComparisonWidget
          title="Customer Sentiment"
          icon={FaSmile}
          iconColor="purple"
          favoriteValue={favoriteMetrics?.sentiment}
          marketAvg={marketMetrics?.sentiment.avg}
          marketLeader={marketMetrics?.sentiment.max}
          leaderName={marketMetrics?.sentiment.leader?.name}
          unit="%"
          format="decimal"
          cardBg={cardBg}
          borderColor={borderColor}
          statBg={statBg}
        />

        {/* Rating */}
        <ComparisonWidget
          title="Average Rating"
          icon={FaTrophy}
          iconColor="orange"
          favoriteValue={favoriteMetrics?.rating}
          marketAvg={marketMetrics?.rating.avg}
          marketLeader={marketMetrics?.rating.max}
          leaderName={marketMetrics?.rating.leader?.name}
          format="decimal"
          cardBg={cardBg}
          borderColor={borderColor}
          statBg={statBg}
        />

        {/* Web Traffic */}
        <ComparisonWidget
          title="Web Traffic"
          icon={FaGlobe}
          iconColor="blue"
          favoriteValue={favoriteMetrics?.traffic}
          marketAvg={marketMetrics?.traffic.avg}
          marketLeader={marketMetrics?.traffic.max}
          leaderName={marketMetrics?.traffic.leader?.name}
          unit=" visits"
          format="number"
          cardBg={cardBg}
          borderColor={borderColor}
          statBg={statBg}
        />

        {/* SEO Score */}
        <ComparisonWidget
          title="SEO Score"
          icon={FaMedal}
          iconColor="teal"
          favoriteValue={favoriteMetrics?.seo}
          marketAvg={marketMetrics?.seo.avg}
          marketLeader={marketMetrics?.seo.max}
          leaderName={marketMetrics?.seo.leader?.name}
          format="decimal"
          cardBg={cardBg}
          borderColor={borderColor}
          statBg={statBg}
        />
      </SimpleGrid>

      {/* Review Growth Chart */}
      <Box
        bg={glassBg}
        backdropFilter={backdropFilter}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        p={6}
        mb={8}
        shadow={premiumShadow}
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        _hover={{ shadow: premiumShadowHover }}
        position="relative"
        overflow="hidden"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          bgGradient: 'linear(to-r, blue.400, cyan.400)',
        }}
      >
        <Heading size="md" mb={4} color={headingTextColor}>Review Growth Trend</Heading>
        <Box height="300px">
          {growthChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={growthChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#4A5568' }} />
                <YAxis tick={{ fontSize: 12, fill: '#4A5568' }} />
                <RechartsTooltip
                  contentStyle={{ backgroundColor: cardBg, border: `1px solid ${borderColor}`, borderRadius: '8px' }}
                />
                <Bar dataKey="reviews" fill="#4299E1" radius={[4, 4, 0, 0]} />
                {marketMetrics && (
                  <ReferenceLine
                    y={marketMetrics.growth.avg / 3}
                    stroke="#ED8936"
                    strokeDasharray="5 5"
                    label={{ value: 'Market Avg', fill: '#ED8936', fontSize: 12 }}
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Flex h="100%" align="center" justify="center">
              <Text color="gray.400">No review data available</Text>
            </Flex>
          )}
        </Box>
      </Box>

      {/* Seasonality Comparison Chart */}
      <Box
        bg={glassBg}
        backdropFilter={backdropFilter}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        p={6}
        mb={8}
        shadow={premiumShadow}
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        _hover={{ shadow: premiumShadowHover }}
        position="relative"
        overflow="hidden"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          bgGradient: 'linear(to-r, purple.400, blue.400, green.400)',
        }}
      >
        <VStack align="stretch" spacing={4}>
          <HStack justify="space-between" align="start">
            <VStack align="start" spacing={1}>
              <HStack spacing={2}>
                <Flex
                  w="36px"
                  h="36px"
                  bgGradient="linear(135deg, purple.100, blue.100)"
                  borderRadius="lg"
                  justify="center"
                  align="center"
                  shadow="sm"
                >
                  <Icon as={FaCalendarAlt} color="purple.500" boxSize={4} />
                </Flex>
                <Heading size="md" color={headingTextColor}>Seasonality Comparison</Heading>
              </HStack>
              <Text fontSize="sm" color={subtleTextColor} maxW="lg">
                Compare your business's traffic and review patterns against industry-wide trends.
                Lines are normalized to highlight seasonal patterns rather than absolute numbers.
              </Text>
            </VStack>
          </HStack>
          
          {/* Legend explanation */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <HStack 
              p={3} 
              bg={statBg} 
              borderRadius="lg" 
              borderWidth="1px" 
              borderColor={borderColor}
              spacing={3}
            >
              <Box w="4px" h="40px" bg="#9F7AEA" borderRadius="full" />
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" fontWeight="semibold" color={headingTextColor}>Industry Trend</Text>
                <Text fontSize="xs" color={subtleTextColor}>All businesses combined</Text>
              </VStack>
            </HStack>
            <HStack 
              p={3} 
              bg={statBg} 
              borderRadius="lg" 
              borderWidth="1px" 
              borderColor={borderColor}
              spacing={3}
            >
              <Box w="4px" h="40px" bg="#4299E1" borderRadius="full" />
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" fontWeight="semibold" color={headingTextColor}>Website Traffic</Text>
                <Text fontSize="xs" color={subtleTextColor}>Your organic search clicks</Text>
              </VStack>
            </HStack>
            <HStack 
              p={3} 
              bg={statBg} 
              borderRadius="lg" 
              borderWidth="1px" 
              borderColor={borderColor}
              spacing={3}
            >
              <Box w="4px" h="40px" bg="#48BB78" borderRadius="full" />
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" fontWeight="semibold" color={headingTextColor}>Your Reviews</Text>
                <Text fontSize="xs" color={subtleTextColor}>Monthly review volume</Text>
              </VStack>
            </HStack>
          </SimpleGrid>
          
          <SeasonalityComparisonChart
            industryData={industrySeasonalityData}
            trafficData={trafficSeasonalityData}
            reviewData={reviewSeasonalityData}
            cardBg={cardBg}
            borderColor={borderColor}
          />
          
          {/* Insight callout */}
          <Box
            p={4}
            bg={insightBg}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={insightBorderColor}
          >
            <HStack spacing={3}>
              <Icon as={FaChartLine} color="purple.500" boxSize={5} />
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" fontWeight="semibold" color={headingTextColor}>
                  How to Read This Chart
                </Text>
                <Text fontSize="xs" color={subtleTextColor}>
                  When your lines diverge from the industry trend, it indicates unique seasonality in your business. 
                  Peaks show high-activity months. Track whether your traffic converts to reviews.
                </Text>
              </VStack>
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* Sentiment Breakdown */}
      <Box
        bg={glassBg}
        backdropFilter={backdropFilter}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        p={6}
        mb={8}
        shadow={premiumShadow}
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        _hover={{ shadow: premiumShadowHover }}
        position="relative"
        overflow="hidden"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          bgGradient: 'linear(to-r, green.400, yellow.400, red.400)',
        }}
      >
        <Heading size="md" mb={4} color={headingTextColor}>Sentiment Breakdown</Heading>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Box 
            bgGradient="linear(135deg, green.50, green.100)"
            p={5} 
            borderRadius="xl" 
            textAlign="center"
            shadow="sm"
            transition="all 0.2s"
            _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
            borderWidth="1px"
            borderColor="green.100"
          >
            <Flex 
              w="48px" 
              h="48px" 
              bg="green.100" 
              borderRadius="full" 
              justify="center" 
              align="center" 
              mx="auto" 
              mb={3}
              shadow="sm"
            >
              <Icon as={FaSmile} color="green.500" boxSize={6} />
            </Flex>
            <Text fontSize="2xl" fontWeight="bold" color="green.600">
              {favoriteMetrics?.positiveReviews.toLocaleString()}
            </Text>
            <Text fontSize="sm" color="green.600" fontWeight="medium">Positive Reviews</Text>
          </Box>
          <Box 
            bgGradient="linear(135deg, yellow.50, orange.50)"
            p={5} 
            borderRadius="xl" 
            textAlign="center"
            shadow="sm"
            transition="all 0.2s"
            _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
            borderWidth="1px"
            borderColor="yellow.100"
          >
            <Flex 
              w="48px" 
              h="48px" 
              bg="yellow.100" 
              borderRadius="full" 
              justify="center" 
              align="center" 
              mx="auto" 
              mb={3}
              shadow="sm"
            >
              <Icon as={FaMeh} color="yellow.500" boxSize={6} />
            </Flex>
            <Text fontSize="2xl" fontWeight="bold" color="yellow.600">
              {favoriteMetrics?.neutralReviews.toLocaleString()}
            </Text>
            <Text fontSize="sm" color="yellow.600" fontWeight="medium">Neutral Reviews</Text>
          </Box>
          <Box 
            bgGradient="linear(135deg, red.50, pink.50)"
            p={5} 
            borderRadius="xl" 
            textAlign="center"
            shadow="sm"
            transition="all 0.2s"
            _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
            borderWidth="1px"
            borderColor="red.100"
          >
            <Flex 
              w="48px" 
              h="48px" 
              bg="red.100" 
              borderRadius="full" 
              justify="center" 
              align="center" 
              mx="auto" 
              mb={3}
              shadow="sm"
            >
              <Icon as={FaFrown} color="red.500" boxSize={6} />
            </Flex>
            <Text fontSize="2xl" fontWeight="bold" color="red.600">
              {favoriteMetrics?.negativeReviews.toLocaleString()}
            </Text>
            <Text fontSize="sm" color="red.600" fontWeight="medium">Negative Reviews</Text>
          </Box>
        </SimpleGrid>
      </Box>

      {/* Biggest Competitors Section */}
      {(closestCompetitors.ahead || closestCompetitors.behind) && (
        <Box mb={8}>
          <Heading size="md" mb={4} color={headingTextColor}>
            <HStack spacing={2}>
              <Icon as={FaTrophy} color="orange.400" />
              <Text>Your Biggest Competitors</Text>
            </HStack>
          </Heading>
          <Text fontSize="sm" color={subtleTextColor} mb={4}>
            These businesses are closest to you in 3-month review growth. Keep an eye on them!
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {/* Competitor Ahead */}
            {closestCompetitors.ahead ? (
              <CompetitorCard
                business={closestCompetitors.ahead}
                imageUrl={businessImages[closestCompetitors.ahead.maps_uri]}
                reviewGrowth={closestCompetitors.ahead.reviewGrowth}
                favoriteGrowth={favoriteMetrics?.growth || 0}
                position="ahead"
                cardBg={cardBg}
                borderColor={borderColor}
              />
            ) : (
              <Box
                bg={glassBg}
                backdropFilter={backdropFilter}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="xl"
                p={6}
                textAlign="center"
                shadow={premiumShadow}
              >
                <Flex
                  w="48px"
                  h="48px"
                  bgGradient="linear(135deg, green.100, green.200)"
                  borderRadius="full"
                  justify="center"
                  align="center"
                  mx="auto"
                  mb={3}
                >
                  <Icon as={FaTrophy} color="green.500" boxSize={5} />
                </Flex>
                <Text fontWeight="bold" color={headingTextColor} mb={1}>
                  You're Leading!
                </Text>
                <Text fontSize="sm" color={subtleTextColor}>
                  No business has higher review growth than you.
                </Text>
              </Box>
            )}

            {/* Competitor Behind */}
            {closestCompetitors.behind ? (
              <CompetitorCard
                business={closestCompetitors.behind}
                imageUrl={businessImages[closestCompetitors.behind.maps_uri]}
                reviewGrowth={closestCompetitors.behind.reviewGrowth}
                favoriteGrowth={favoriteMetrics?.growth || 0}
                position="behind"
                cardBg={cardBg}
                borderColor={borderColor}
              />
            ) : (
              <Box
                bg={glassBg}
                backdropFilter={backdropFilter}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="xl"
                p={6}
                textAlign="center"
                shadow={premiumShadow}
              >
                <Flex
                  w="48px"
                  h="48px"
                  bgGradient="linear(135deg, yellow.100, orange.100)"
                  borderRadius="full"
                  justify="center"
                  align="center"
                  mx="auto"
                  mb={3}
                >
                  <Icon as={FaChartLine} color="orange.500" boxSize={5} />
                </Flex>
                <Text fontWeight="bold" color={headingTextColor} mb={1}>
                  Room to Grow
                </Text>
                <Text fontSize="sm" color={subtleTextColor}>
                  No business behind you - focus on catching the leaders!
                </Text>
              </Box>
            )}
          </SimpleGrid>
        </Box>
      )}

      {/* Business Selector Modal */}
      <Modal 
        isOpen={isSelectModalOpen} 
        onClose={() => {
          onSelectModalClose();
          setModalSearchQuery('');
        }}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent maxH="80vh" borderRadius="2xl" shadow="2xl">
          <ModalHeader borderBottom="1px" borderColor={borderColor}>
            <VStack align="start" spacing={2}>
              <Heading size="md" color={headingTextColor}>Select Your Favorite Business</Heading>
              <Text fontSize="sm" color={subtleTextColor} fontWeight="normal">
                Choose one business to track and compare against the market.
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} pt={4}>
            {/* Search Input */}
            <InputGroup mb={4}>
              <InputLeftElement pointerEvents="none">
                <Icon as={FaSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search businesses..."
                value={modalSearchQuery}
                onChange={(e) => setModalSearchQuery(e.target.value)}
                borderRadius="xl"
                _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
              />
            </InputGroup>
            
            {/* Business Grid */}
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
              {filteredBusinessesForModal.slice(0, 30).map(business => (
                <BusinessSelectorCard
                  key={business.maps_uri}
                  business={business}
                  imageUrl={businessImages[business.maps_uri]}
                  isSelected={business.maps_uri === selectedBusinessUri}
                  onSelect={handleSelectFavorite}
                  cardBg={cardBg}
                  borderColor={borderColor}
                />
              ))}
            </SimpleGrid>
            
            {filteredBusinessesForModal.length === 0 && (
              <Box textAlign="center" py={8}>
                <Text color={subtleTextColor}>No businesses match your search</Text>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default FavoriteDashboard;
